import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Send, 
  Users,
  AlertTriangle,
  AlertCircle,
  Info,
  Clock,
  Mail,
  MailOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { adminService } from '../../services/adminService';
import { toast } from 'sonner';

const priorityConfig = {
  critical: { label: 'Critical', color: 'bg-red-500 text-white', icon: AlertTriangle },
  urgent: { label: 'Urgent', color: 'bg-orange-500 text-white', icon: AlertCircle },
  important: { label: 'Important', color: 'bg-blue-500 text-white', icon: Info },
  normal: { label: 'Normal', color: 'bg-gray-500 text-white', icon: MessageSquare },
};

const AdminMessages = () => {
  const [clients, setClients] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    client_account_id: 'broadcast',
    subject: '',
    body: '',
    priority: 'normal'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsData, messagesData] = await Promise.all([
        adminService.getClients(),
        adminService.getMessageHistory()
      ]);
      setClients(clientsData);
      setMessageHistory(messagesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.body.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }

    setSending(true);
    try {
      const messageData = {
        ...formData,
        client_account_id: formData.client_account_id === 'broadcast' ? null : formData.client_account_id
      };

      await adminService.sendMessage(messageData);
      
      toast.success(
        formData.client_account_id 
          ? 'Message sent to client' 
          : 'Broadcast sent to all clients'
      );

      // Reset form
      setFormData({
        client_account_id: 'broadcast',
        subject: '',
        body: '',
        priority: 'normal'
      });

      // Reload message history
      const messagesData = await adminService.getMessageHistory();
      setMessageHistory(messagesData);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-messages">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Send Message Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-amber-500" />
              Send Message
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendMessage} className="space-y-4">
              <div>
                <Label htmlFor="recipient">Recipient</Label>
                <Select 
                  value={formData.client_account_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, client_account_id: value }))}
                >
                  <SelectTrigger className="mt-1" data-testid="recipient-select">
                    <Users className="h-4 w-4 mr-2 text-gray-400" />
                    <SelectValue placeholder="Select recipient" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="broadcast">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        All Clients (Broadcast)
                      </div>
                    </SelectItem>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to send to all clients
                </p>
              </div>

              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger className="mt-1" data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(priorityConfig).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {config.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Critical/Urgent messages show alerts on client dashboard
                </p>
              </div>

              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Message subject"
                  className="mt-1"
                  data-testid="subject-input"
                />
              </div>

              <div>
                <Label htmlFor="body">Message</Label>
                <Textarea
                  id="body"
                  value={formData.body}
                  onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Type your message here..."
                  className="mt-1"
                  rows={6}
                  data-testid="body-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-amber-500 hover:bg-amber-600"
                disabled={sending}
                data-testid="send-btn"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Message History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Recent Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {messageHistory.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No messages sent yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {messageHistory.map((message) => {
                  const priority = priorityConfig[message.priority] || priorityConfig.normal;
                  const PriorityIcon = priority.icon;
                  
                  return (
                    <div 
                      key={message.id} 
                      className="p-3 bg-gray-50 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-md hover:bg-gray-100"
                      style={{ borderLeftColor: priority.color.includes('red') ? '#ef4444' : 
                               priority.color.includes('orange') ? '#f97316' :
                               priority.color.includes('blue') ? '#3b82f6' : '#6b7280' }}
                      onClick={() => { setSelectedMessage(message); setDialogOpen(true); }}
                      data-testid={`admin-message-${message.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                          <Badge className={priority.color}>
                            <PriorityIcon className="h-3 w-3 mr-1" />
                            {priority.label}
                          </Badge>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(message.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="font-medium text-gray-900 text-sm">{message.subject}</p>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {message.client_name}
                      </p>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{message.body}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Templates */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Templates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { subject: 'Holiday Schedule', body: 'Please note our holiday schedule...', priority: 'important' },
              { subject: 'Price Update', body: 'We are adjusting prices effective...', priority: 'urgent' },
              { subject: 'New Product Alert', body: 'We are excited to announce...', priority: 'normal' },
              { subject: 'Delivery Update', body: 'Due to weather conditions...', priority: 'critical' },
            ].map((template) => (
              <Button
                key={template.subject}
                variant="outline"
                className="h-auto py-3 flex flex-col items-start text-left"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  subject: template.subject,
                  body: template.body,
                  priority: template.priority
                }))}
              >
                <Badge className={`mb-1 ${priorityConfig[template.priority].color}`}>
                  {priorityConfig[template.priority].label}
                </Badge>
                <span className="font-medium">{template.subject}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Message Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-message-detail-dialog">
          {selectedMessage && (() => {
            const priority = priorityConfig[selectedMessage.priority] || priorityConfig.normal;
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2">
                    <Badge className={priority.color}>
                      {priority.label}
                    </Badge>
                    <DialogTitle className="text-lg">{selectedMessage.subject}</DialogTitle>
                    <DialogDescription className="sr-only"> dialog. Use the form below to continue.</DialogDescription>
                  </div>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {selectedMessage.client_name}
                    </span>
                    <span>
                      {new Date(selectedMessage.created_at).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.body}</p>
                  </div>
                  <div className="flex justify-end pt-4 border-t">
                    <Button
                      className="bg-amber-500 hover:bg-amber-600"
                      onClick={() => setDialogOpen(false)}
                      data-testid="admin-message-detail-close"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMessages;
